import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../contexts/MessageContext';
import ChatWindow from '../../components/chat/ChatWindow';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 4rem);
  
  @media (min-width: 992px) {
    height: calc(100vh - 2rem);
  }
`;

const Title = styled.h1`
  margin-bottom: 1.5rem;
`;

const ChatContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ClientsList = styled.div`
  width: 280px;
  background-color: white;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const ClientItem = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  background-color: ${({ $isActive, $hasUnread, theme }) => 
    $isActive ? theme.colors.backgroundLight : 
    $hasUnread ? 'rgba(249, 237, 237, 0.5)' : 'white'};
  position: relative;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const ClientHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const ClientName = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const LastMessage = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnreadBadge = styled.span`
  display: inline-block;
  background-color: #f44336;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-left: 8px;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  padding: 2rem;
`;

const ChatSection = styled.div`
  flex: 1;
  overflow: hidden;
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ChatWithClients = () => {
  const { currentUser } = useAuth();
  const { markAsRead } = useMessages();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchClients = async () => {
      try {
        setLoading(true);
        
        // Get clients linked to this partner
        const clientsRef = collection(db, 'partners', currentUser.uid, 'clients');
        const clientsSnapshot = await getDocs(clientsRef);
        
        const clientsData = [];
        
        // Fetch basic info about each client
        for (const clientDoc of clientsSnapshot.docs) {
          const clientId = clientDoc.id;
          const userDoc = await getDocs(query(
            collection(db, 'users'),
            where('uid', '==', clientId)
          ));
          
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            
            // Check for unread messages
            const messagesQuery = query(
              collection(db, 'messages'),
              where('senderId', '==', clientId),
              where('receiverId', '==', currentUser.uid),
              where('isRead', '==', false)
            );
            
            const unreadSnapshot = await getDocs(messagesQuery);
            const unreadCount = unreadSnapshot.size;
            
            clientsData.push({
              id: clientId,
              displayName: userData.displayName || 'Unnamed Client',
              email: userData.email,
              unreadCount: unreadCount
            });
          }
        }
        
        setClients(clientsData);
        
        // Select first client by default if available
        if (clientsData.length > 0 && !selectedClientId) {
          setSelectedClientId(clientsData[0].id);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClients();
    
    // Set up real-time listener for conversations
    const conversationsRef = collection(db, 'conversations');
    const q = query(conversationsRef, where('partnerId', '==', currentUser.uid));
    
    const unsubscribe = onSnapshot(q, () => {
      // Refresh client list when conversations change
      fetchClients();
    });
    
    return () => unsubscribe();
  }, [currentUser, selectedClientId]);
  
  const selectClient = (clientId) => {
    setSelectedClientId(clientId);
    
    // Find the conversation and mark it as read
    const findAndMarkConversation = async () => {
      try {
        const conversationsRef = collection(db, 'conversations');
        const q = query(
          conversationsRef,
          where('userId', '==', clientId),
          where('partnerId', '==', currentUser.uid)
        );
        
        const conversationsSnapshot = await getDocs(q);
        if (!conversationsSnapshot.empty) {
          const conversationId = conversationsSnapshot.docs[0].id;
          markAsRead(conversationId);
        }
      } catch (error) {
        console.error('Error finding conversation:', error);
      }
    };
    
    findAndMarkConversation();
  };
  
  if (loading) {
    return <LoadingMessage>Loading client conversations...</LoadingMessage>;
  }
  
  return (
    <PageContainer>
      <Title>Client Messages</Title>
      
      <ChatContainer>
        <ClientsList>
          {clients.length === 0 ? (
            <EmptyState>
              <p>No clients found</p>
              <p>Once you have clients, their conversations will appear here</p>
            </EmptyState>
          ) : (
            clients.map(client => (
              <ClientItem 
                key={client.id} 
                $isActive={selectedClientId === client.id}
                $hasUnread={client.unreadCount > 0}
                onClick={() => selectClient(client.id)}
              >
                <ClientHeader>
                  <ClientName>
                    {client.displayName}
                  </ClientName>
                  {client.unreadCount > 0 && (
                    <UnreadBadge>{client.unreadCount}</UnreadBadge>
                  )}
                </ClientHeader>
                <LastMessage>{client.email}</LastMessage>
              </ClientItem>
            ))
          )}
        </ClientsList>
        
        <ChatSection>
          {selectedClientId ? (
            <ChatWindow 
              partnerId={currentUser.uid} 
              clientId={selectedClientId} 
              isPartnerView={true}
            />
          ) : (
            <EmptyState>
              <p>Select a client to start chatting</p>
            </EmptyState>
          )}
        </ChatSection>
      </ChatContainer>
    </PageContainer>
  );
};

export default ChatWithClients; 